import { NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { RoleGuard } from './core/guards/role.guard';
import { DefaultRouteGuard } from './core/guards/default-route.guard';
import { LoginComponent } from './features/auth/login/login.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { Permission } from './core/permissions';

const withPermission = (permission: Permission) => ({
  canActivate: [RoleGuard],
  data: { permission },
});

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [RoleGuard],
    children: [
      {
        path: '',
        canActivate: [DefaultRouteGuard],
        children: [],
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule),
        ...withPermission('dashboard.view'),
      },
      {
        path: 'pos',
        redirectTo: 'billing',
        pathMatch: 'full',
      },
      {
        path: 'billing',
        loadChildren: () => import('./features/billing/billing.module').then(m => m.BillingModule),
        ...withPermission('billing.access'),
      },
      {
        path: 'products',
        loadChildren: () => import('./features/products/products.module').then(m => m.ProductsModule),
        ...withPermission('products.view'),
      },
      {
        path: 'categories',
        loadChildren: () => import('./features/categories/categories.module').then(m => m.CategoriesModule),
        ...withPermission('categories.manage'),
      },
      {
        path: 'reports',
        loadChildren: () => import('./features/reports/reports.module').then(m => m.ReportsModule),
        ...withPermission('reports.view'),
      },
      {
        path: 'orders',
        loadChildren: () => import('./features/orders/orders.module').then(m => m.OrdersModule),
        ...withPermission('orders.access'),
      },
      {
        path: 'printer-settings',
        loadComponent: () => import('./features/printer-settings-page/printer-settings-page.component').then(m => m.PrinterSettingsPageComponent),
        ...withPermission('printer.manage'),
      },
      {
        path: 'settings',
        loadChildren: () => import('./features/settings/settings.module').then(m => m.SettingsModule),
        ...withPermission('settings.manage'),
      },
      {
        path: 'customers',
        loadChildren: () => import('./features/customers/customers.module').then(m => m.CustomersModule),
        ...withPermission('customers.access'),
      },
      {
        path: 'transactions',
        loadChildren: () => import('./features/transactions/transactions.module').then(m => m.TransactionsModule),
        ...withPermission('customers.access'),
      },
      {
        path: 'expenses',
        loadChildren: () => import('./features/expenses/expenses.module').then(m => m.ExpensesModule),
        ...withPermission('expenses.manage'),
      },
      {
        path: 'purchases',
        loadChildren: () => import('./features/purchases/purchases.module').then(m => m.PurchasesModule),
        ...withPermission('purchases.access'),
      },
      {
        path: 'purchase',
        loadChildren: () => import('./features/purchase/purchase.module').then(m => m.PurchaseModule),
        ...withPermission('purchase.access'),
      },
      {
        path: 'vendors',
        loadChildren: () => import('./features/vendors/vendors.module').then(m => m.VendorsModule),
        ...withPermission('vendors.manage'),
      },
      {
        path: 'units',
        loadChildren: () => import('./features/units/units.module').then(m => m.UnitsModule),
        ...withPermission('units.manage'),
      },
      {
        path: 'users',
        loadChildren: () => import('./features/users/users.module').then(m => m.UsersModule),
        ...withPermission('users.manage'),
      },
    ],
  },
  {
    path: '**',
    canActivate: [DefaultRouteGuard],
    children: [],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
